import { useMemo } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { commentsEndpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { selectUserId } from '@/redux/user/selectors';
import type { Comment, CommentsResponse } from '@/shared/types/comments';
import { EventName } from '@/shared/types/segment';

const API = Object.freeze({
  listComments: (deskId: string, sessionId: string): Promise<CommentsResponse> =>
    callGet(commentsEndpoints.comments(deskId, sessionId)),
  createComment: (deskId: string, sessionId: string, text: string): Promise<Comment> =>
    callPost(commentsEndpoints.comments(deskId, sessionId), { text }),
  updateComment: (
    deskId: string,
    sessionId: string,
    commentId: string,
    text: string
  ): Promise<Comment> => callPut(commentsEndpoints.comment(deskId, sessionId, commentId), { text }),
  deleteComment: (
    deskId: string,
    sessionId: string,
    commentId: string
  ): Promise<{ comment_id: string }> =>
    callDelete(commentsEndpoints.comment(deskId, sessionId, commentId)),
});

export const commentKeys = {
  list: (deskId: string, sessionId: string) => [commentsEndpoints.comments(deskId, sessionId)],
};

export const onCommentCreated = (queryClient: QueryClient, deskId: string, comment: Comment) => {
  const key = commentKeys.list(deskId, comment.session_id);
  queryClient.setQueryData<CommentsResponse>(key, (old) => ({
    comments: [...(old?.comments ?? []), comment],
    total: (old?.total ?? 0) + 1,
  }));
};

export const onCommentRemoved = (
  queryClient: QueryClient,
  deskId: string,
  sessionId: string,
  commentId: string
) => {
  const key = commentKeys.list(deskId, sessionId);
  queryClient.setQueryData<CommentsResponse>(key, (old) => {
    if (!old) {
      return old;
    }
    const filteredComments = old.comments.filter((c) => c.comment_id !== commentId);
    const wasRemoved = filteredComments.length !== old.comments.length;
    return {
      comments: filteredComments,
      total: wasRemoved ? Math.max(0, old.total - 1) : old.total,
    };
  });
};

/**
 * CRUD operations for internal conversation notes (comments).
 *
 * Notes are agent-only annotations on a conversation — the end-user never sees them.
 * Create and delete use optimistic updates with rollback on error.
 */
export const useComments = (deskId: string, sessionId: string) => {
  const queryClient = useQueryClient();
  const userId = useSelector(selectUserId);

  const { data, status } = useQuery<CommentsResponse>({
    queryKey: commentKeys.list(deskId, sessionId),
    queryFn: () => API.listComments(deskId, sessionId),
    enabled: !!deskId && !!sessionId,
  });

  const { mutate: createComment } = useMutation<
    Comment,
    Error,
    string,
    { previous?: CommentsResponse }
  >({
    mutationFn: (text) => API.createComment(deskId, sessionId, text),
    meta: {
      segment: {
        success: EventName.NoteAdded,
        action: 'note_create',
        successProps: (created: Comment) => ({ session_id: created.session_id }),
      },
    },
    onMutate: async (text) => {
      await queryClient.cancelQueries({ queryKey: commentKeys.list(deskId, sessionId) });
      const previous = queryClient.getQueryData<CommentsResponse>(
        commentKeys.list(deskId, sessionId)
      );
      const now = new Date().toISOString();
      const optimistic: Comment = {
        comment_id: `temp-${Date.now()}`,
        session_id: sessionId,
        agent_id: userId,
        text,
        created: now,
        updated: now,
      };
      queryClient.setQueryData<CommentsResponse>(commentKeys.list(deskId, sessionId), (old) => ({
        comments: [...(old?.comments ?? []), optimistic],
        total: (old?.total ?? 0) + 1,
      }));
      return { previous };
    },
    onError: (err, _text, context) => {
      if (context?.previous) {
        queryClient.setQueryData(commentKeys.list(deskId, sessionId), context.previous);
      }
      toast.error(err.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.list(deskId, sessionId) });
    },
  });

  const { mutate: updateComment } = useMutation<
    Comment,
    Error,
    { commentId: string; text: string }
  >({
    mutationFn: ({ commentId, text }) => API.updateComment(deskId, sessionId, commentId, text),
    meta: { errorToast: true },
    onSuccess: (updated) => {
      const key = commentKeys.list(deskId, sessionId);
      queryClient.setQueryData<CommentsResponse>(key, (old) => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          comments: old.comments.map((c) => (c.comment_id === updated.comment_id ? updated : c)),
        };
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.list(deskId, sessionId) });
    },
  });

  const { mutate: deleteComment } = useMutation<
    { comment_id: string },
    Error,
    string,
    { previous?: CommentsResponse }
  >({
    mutationFn: (commentId) => API.deleteComment(deskId, sessionId, commentId),
    onMutate: async (commentId) => {
      await queryClient.cancelQueries({ queryKey: commentKeys.list(deskId, sessionId) });
      const previous = queryClient.getQueryData<CommentsResponse>(
        commentKeys.list(deskId, sessionId)
      );
      onCommentRemoved(queryClient, deskId, sessionId, commentId);
      return { previous };
    },
    onError: (err, _commentId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(commentKeys.list(deskId, sessionId), context.previous);
      }
      toast.error(err.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.list(deskId, sessionId) });
    },
  });

  const comments = useMemo(() => {
    const list = data?.comments ?? [];
    return [...list].sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  }, [data?.comments]);

  return { comments, total: data?.total ?? 0, status, createComment, updateComment, deleteComment };
};
